import { useState, useMemo, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, Calendar as CalendarIcon, X, ChevronDown, Search } from 'lucide-react';

export default function WorklogSummary({ 
  worklogsData = [], 
  requestsData = [], 
  tasksData = [], 
  usersData = [],
  loading 
}) {
  // Load saved filters from sessionStorage
  const loadSavedFilters = () => {
    const saved = sessionStorage.getItem('worklogSummaryFilters');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return {};
      }
    }
    return {};
  };

  const savedFilters = loadSavedFilters();

  // State for grouping controls
  const [groupBy, setGroupBy] = useState(savedFilters.groupBy || 'project-user'); // project-user, user-project, task-user, project-task
  const [periodGrouping, setPeriodGrouping] = useState(savedFilters.periodGrouping || 'months'); // days, weeks, months
  
  // Filter states
  const [selectedProjects, setSelectedProjects] = useState(savedFilters.selectedProjects || []);
  const [selectedUsers, setSelectedUsers] = useState(savedFilters.selectedUsers || []);
  
  // Dropdown visibility states
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  
  // Search within dropdowns
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  
  // Refs for click outside detection
  const projectDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target)) {
        setShowProjectDropdown(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Default to current month
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  
  const [startDate, setStartDate] = useState(savedFilters.startDate || firstDayOfMonth.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(savedFilters.endDate || lastDayOfMonth.toISOString().split('T')[0]);

  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    const filters = {
      groupBy,
      periodGrouping,
      startDate,
      endDate,
      selectedProjects,
      selectedUsers
    };
    sessionStorage.setItem('worklogSummaryFilters', JSON.stringify(filters));
  }, [groupBy, periodGrouping, startDate, endDate, selectedProjects, selectedUsers]);

  // Parse duration string to hours
  const parseDuration = (worklog) => {
    // Always use duration field first
    const durationValue = worklog.duration || worklog.timespent || worklog.time_decimal || worklog.hours || worklog.time_spent || worklog.duration_decimal;
    
    if (!durationValue) {
      return 0;
    }
    
    // If it's already a number, return it
    if (typeof durationValue === 'number') {
      return durationValue;
    }
    
    // If it's an object (PostgreSQL interval), try to extract hours
    if (typeof durationValue === 'object' && durationValue !== null) {
      // PostgreSQL interval object might have hours, minutes, seconds properties
      const hours = durationValue.hours || 0;
      const minutes = durationValue.minutes || 0;
      const seconds = durationValue.seconds || 0;
      const days = durationValue.days || 0;
      return days * 24 + hours + minutes / 60 + seconds / 3600;
    }
    
    // Convert to string if it's not already
    const str = String(durationValue);
    
    let totalHours = 0;
    
    // Parse "1h 0m" or "2h 30m" format
    const hourMinMatch = str.match(/(\d+)h\s*(\d+)m/i);
    if (hourMinMatch) {
      totalHours = parseInt(hourMinMatch[1]) + parseInt(hourMinMatch[2]) / 60;
      return totalHours;
    }
    
    // Parse "X day" format
    const dayMatch = str.match(/(\d+)\s*day/i);
    if (dayMatch) {
      totalHours += parseInt(dayMatch[1]) * 24;
    }
    
    // Parse "HH:MM:SS" format
    const timeMatch = str.match(/(\d{1,2}):(\d{2}):(\d{2})/);
    if (timeMatch) {
      totalHours += parseInt(timeMatch[1]);
      totalHours += parseInt(timeMatch[2]) / 60;
      totalHours += parseInt(timeMatch[3]) / 3600;
    }
    
    return totalHours;
  };

  // Format decimal hours to HH:MM
  const formatHours = (decimalHours) => {
    if (!decimalHours || decimalHours === 0) return '';
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };
  
  // Filter helper functions
  const toggleSelection = (type, value) => {
    if (type === 'projects') {
      setSelectedProjects(prev => 
        prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value]
      );
    } else if (type === 'users') {
      setSelectedUsers(prev => 
        prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value]
      );
    }
  };

  const removeSelection = (type, value) => {
    if (type === 'projects') {
      setSelectedProjects(prev => prev.filter(item => item !== value));
    } else if (type === 'users') {
      setSelectedUsers(prev => prev.filter(item => item !== value));
    }
  };

  const clearAllFilters = () => {
    setSelectedProjects([]);
    setSelectedUsers([]);
  };
  
  const activeFilterCount = selectedProjects.length + selectedUsers.length;

  // Create lookup maps
  const taskLookup = useMemo(() => {
    const map = new Map();
    tasksData.forEach(task => map.set(task.id, task));
    return map;
  }, [tasksData]);

  const requestLookup = useMemo(() => {
    const map = new Map();
    requestsData.forEach(req => map.set(req.id, req));
    return map;
  }, [requestsData]);

  const userLookup = useMemo(() => {
    const map = new Map();
    usersData.forEach(user => map.set(user.id, user));
    return map;
  }, [usersData]);

  // Filter worklogs by date range, projects, and users
  const filteredWorklogs = useMemo(() => {
    return worklogsData.filter(log => {
      if (!log.start_date) return false;
      const logDate = new Date(log.start_date);
      const start = new Date(startDate);
      const end = new Date(endDate);
      // Set end date to end of day (23:59:59.999) to include all logs on that date
      end.setHours(23, 59, 59, 999);
      
      if (logDate < start || logDate > end) return false;
      
      // User filter
      if (selectedUsers.length > 0 && !selectedUsers.includes(log.created_by_id)) return false;
      
      // Project filter
      if (selectedProjects.length > 0) {
        const task = taskLookup.get(log.object_id);
        const request = task ? requestLookup.get(task.object_id) : null;
        const projectNo = request?.eng_project_number;
        if (!projectNo || !selectedProjects.includes(projectNo)) return false;
      }
      
      return true;
    });
  }, [worklogsData, startDate, endDate, selectedUsers, selectedProjects, taskLookup, requestLookup]);

  // Generate date columns based on period grouping
  const dateColumns = useMemo(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const columns = [];
    
    // Limit to prevent browser freeze
    const MAX_COLUMNS = 100;

    if (periodGrouping === 'days') {
      let current = new Date(start);
      let count = 0;
      while (current <= end && count < MAX_COLUMNS) {
        columns.push({
          key: current.toISOString().split('T')[0],
          label: current.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          date: new Date(current)
        });
        current.setDate(current.getDate() + 1);
        count++;
      }
    } else if (periodGrouping === 'weeks') {
      let current = new Date(start);
      // Start from Monday
      current.setDate(current.getDate() - current.getDay() + 1);
      
      let count = 0;
      while (current <= end && count < MAX_COLUMNS) {
        const weekEnd = new Date(current);
        weekEnd.setDate(weekEnd.getDate() + 6);
        columns.push({
          key: current.toISOString().split('T')[0],
          label: `${current.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
          date: new Date(current),
          weekEnd: weekEnd
        });
        current.setDate(current.getDate() + 7);
        count++;
      }
    } else if (periodGrouping === 'months') {
      let current = new Date(start.getFullYear(), start.getMonth(), 1);
      const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
      
      let count = 0;
      while (current <= endMonth && count < MAX_COLUMNS) {
        columns.push({
          key: `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`,
          label: current.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          date: new Date(current)
        });
        current.setMonth(current.getMonth() + 1);
        count++;
      }
    }

    return columns;
  }, [startDate, endDate, periodGrouping]);

  // Build pivot data structure
  const pivotData = useMemo(() => {
    const data = {};

    filteredWorklogs.forEach(log => {
      const task = taskLookup.get(log.object_id);
      const request = task ? requestLookup.get(task.object_id) : null;
      const user = userLookup.get(log.created_by_id);
      const hours = parseDuration(log);
      
      if (!log.start_date) return;
      const logDate = new Date(log.start_date);
      
      // Determine which date column this log belongs to
      let dateKey = '';
      if (periodGrouping === 'days') {
        dateKey = logDate.toISOString().split('T')[0];
      } else if (periodGrouping === 'weeks') {
        const weekStart = new Date(logDate);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        dateKey = weekStart.toISOString().split('T')[0];
      } else if (periodGrouping === 'months') {
        dateKey = `${logDate.getFullYear()}-${String(logDate.getMonth() + 1).padStart(2, '0')}`;
      }

      // Build hierarchical keys based on groupBy setting
      let primaryKey = '';
      let secondaryKey = '';
      
      if (groupBy === 'project-user') {
        const projectNo = request?.eng_project_number || 'No Project';
        const userName = user ? `${user.first_name} ${user.last_name}` : 'Unknown User';
        primaryKey = projectNo;
        secondaryKey = `${projectNo}|${userName}`;
      } else if (groupBy === 'user-project') {
        const userName = user ? `${user.first_name} ${user.last_name}` : 'Unknown User';
        const projectNo = request?.eng_project_number || 'No Project';
        primaryKey = userName;
        secondaryKey = `${userName}|${projectNo}`;
      } else if (groupBy === 'task-user') {
        const taskNo = task?.number || 'No Task';
        const userName = user ? `${user.first_name} ${user.last_name}` : 'Unknown User';
        primaryKey = taskNo;
        secondaryKey = `${taskNo}|${userName}`;
      } else if (groupBy === 'project-task') {
        const projectNo = request?.eng_project_number || 'No Project';
        const taskNo = task?.number || 'No Task';
        primaryKey = projectNo;
        secondaryKey = `${projectNo}|${taskNo}`;
      }

      // Initialize data structures
      if (!data[primaryKey]) {
        data[primaryKey] = {
          label: primaryKey,
          totals: {},
          children: {}
        };
      }
      if (!data[primaryKey].children[secondaryKey]) {
        data[primaryKey].children[secondaryKey] = {
          label: secondaryKey.split('|')[1],
          totals: {}
        };
      }

      // Add hours to the appropriate date column
      if (!data[primaryKey].totals[dateKey]) {
        data[primaryKey].totals[dateKey] = 0;
      }
      if (!data[primaryKey].children[secondaryKey].totals[dateKey]) {
        data[primaryKey].children[secondaryKey].totals[dateKey] = 0;
      }
      
      data[primaryKey].totals[dateKey] += hours;
      data[primaryKey].children[secondaryKey].totals[dateKey] += hours;
    });

    return data;
  }, [filteredWorklogs, groupBy, periodGrouping, taskLookup, requestLookup, userLookup, dateColumns]);

  // Get unique projects and users
  const uniqueProjects = useMemo(() => {
    const projectSet = new Set();
    worklogsData.forEach(log => {
      const task = taskLookup.get(log.object_id);
      const request = task ? requestLookup.get(task.object_id) : null;
      if (request?.eng_project_number) {
        projectSet.add(request.eng_project_number);
      }
    });
    return Array.from(projectSet).sort();
  }, [worklogsData, taskLookup, requestLookup]);

  // Filtered projects for search
  const filteredProjects = useMemo(() => {
    if (!projectSearchTerm) return uniqueProjects;
    return uniqueProjects.filter(project => 
      project.toLowerCase().includes(projectSearchTerm.toLowerCase())
    );
  }, [uniqueProjects, projectSearchTerm]);

  const uniqueUsers = useMemo(() => {
    const userIds = new Set(worklogsData.map(log => log.created_by_id).filter(Boolean));
    return usersData.filter(user => userIds.has(user.id)).sort((a, b) => 
      `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
    );
  }, [worklogsData, usersData]);

  // Filtered users for search
  const filteredUsers = useMemo(() => {
    if (!userSearchTerm) return uniqueUsers;
    return uniqueUsers.filter(user => {
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
      return fullName.includes(userSearchTerm.toLowerCase());
    });
  }, [uniqueUsers, userSearchTerm]);

  // Calculate grand totals
  const grandTotals = useMemo(() => {
    const totals = {};
    dateColumns.forEach(col => {
      totals[col.key] = 0;
    });

    Object.values(pivotData).forEach(primary => {
      Object.entries(primary.totals).forEach(([dateKey, hours]) => {
        if (totals[dateKey] !== undefined) {
          totals[dateKey] += hours;
        }
      });
    });

    return totals;
  }, [pivotData, dateColumns]);

  // Calculate row totals
  const getRowTotal = (totals) => {
    return Object.values(totals).reduce((sum, hours) => sum + hours, 0);
  };

  // Quick date range presets
  const setDateRangePreset = (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'this-week':
        start = new Date(today);
        start.setDate(today.getDate() - today.getDay() + 1);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;
      case 'this-month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'last-month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'this-year':
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today.getFullYear(), 11, 31);
        break;
      default:
        return;
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading worklog summary...</p>
        </div>
      </div>
    );
  }

  if (!worklogsData || worklogsData.length === 0) {
    return (
      <div className="p-6">
        <h1 className="text-3xl font-bold mb-2">Worklog Summary</h1>
        <p className="text-gray-600">No worklog data available.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex">
      {/* Filters Sidebar */}
      <div className="w-80 bg-white border-r shadow-sm overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-2">Filters</h2>
          <p className="text-sm text-gray-500 mb-6">
            Showing {filteredWorklogs.length.toLocaleString()} of {worklogsData.length.toLocaleString()} worklogs
          </p>

          <div className="space-y-6">
            {/* Date Range */}
            <div className="space-y-2">
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
              />
            </div>

            {/* Quick Date Filters */}
            <div className="space-y-2">
              <Label>Quick Range</Label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setDateRangePreset('this-week')}
                  className="px-2 py-1 text-xs border rounded hover:bg-gray-50"
                >
                  This Week
                </button>
                <button
                  onClick={() => setDateRangePreset('this-month')}
                  className="px-2 py-1 text-xs border rounded hover:bg-gray-50"
                >
                  This Month
                </button>
                <button
                  onClick={() => setDateRangePreset('last-month')}
                  className="px-2 py-1 text-xs border rounded hover:bg-gray-50"
                >
                  Last Month
                </button>
                <button
                  onClick={() => setDateRangePreset('this-year')}
                  className="px-2 py-1 text-xs border rounded hover:bg-gray-50"
                >
                  This Year
                </button>
              </div>
            </div>

            {/* Group By */}
            <div className="space-y-2">
              <Label htmlFor="group-by">Group By</Label>
              <select
                id="group-by"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm"
              >
                <option value="project-user">Project → User</option>
                <option value="user-project">User → Project</option>
                <option value="task-user">Task → User</option>
                <option value="project-task">Project → Task</option>
              </select>
            </div>

            {/* Period Grouping */}
            <div className="space-y-2">
              <Label htmlFor="period">Period Grouping</Label>
              <select
                id="period"
                value={periodGrouping}
                onChange={(e) => setPeriodGrouping(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm"
              >
                <option value="days">Days</option>
                <option value="weeks">Weeks</option>
                <option value="months">Months</option>
              </select>
            </div>

            {/* Active Filters Display */}
            {activeFilterCount > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Active Filters ({activeFilterCount})</Label>
                  <button
                    onClick={clearAllFilters}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Clear All
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedProjects.map(project => (
                    <Badge key={project} variant="secondary" className="flex items-center gap-1 border-l-2 border-blue-500">
                      {project}
                      <X 
                        className="h-3 w-3 cursor-pointer hover:text-destructive" 
                        onClick={() => removeSelection('projects', project)}
                      />
                    </Badge>
                  ))}
                  {selectedUsers.map(userId => {
                    const user = usersData.find(u => u.id === userId);
                    return (
                      <Badge key={userId} variant="secondary" className="flex items-center gap-1 border-l-2 border-green-500">
                        {user ? `${user.first_name} ${user.last_name}` : userId}
                        <X 
                          className="h-3 w-3 cursor-pointer hover:text-destructive" 
                          onClick={() => removeSelection('users', userId)}
                        />
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Projects Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Projects</Label>
              <div className="relative" ref={projectDropdownRef}>
                <button
                  onClick={() => setShowProjectDropdown(!showProjectDropdown)}
                  className="w-full flex items-center justify-between px-3 py-2 border rounded-md text-sm bg-white hover:bg-gray-50"
                >
                  <span className="text-gray-700">
                    {selectedProjects.length > 0 ? `${selectedProjects.length} selected` : 'Select projects'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>
                {showProjectDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-hidden flex flex-col">
                    <div className="p-2 border-b sticky top-0 bg-white">
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                        <Input
                          type="text"
                          placeholder="Search projects..."
                          value={projectSearchTerm}
                          onChange={(e) => setProjectSearchTerm(e.target.value)}
                          className="pl-8 h-9 text-sm"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-52">
                      {filteredProjects.map(project => (
                        <div
                          key={project}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                          onClick={() => toggleSelection('projects', project)}
                        >
                          <Checkbox checked={selectedProjects.includes(project)} />
                          <span className="text-sm">{project}</span>
                        </div>
                      ))}
                      {filteredProjects.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">No projects found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Users Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Users</Label>
              <div className="relative" ref={userDropdownRef}>
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="w-full flex items-center justify-between px-3 py-2 border rounded-md text-sm bg-white hover:bg-gray-50"
                >
                  <span className="text-gray-700">
                    {selectedUsers.length > 0 ? `${selectedUsers.length} selected` : 'Select users'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>
                {showUserDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-hidden flex flex-col">
                    <div className="p-2 border-b sticky top-0 bg-white">
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                        <Input
                          type="text"
                          placeholder="Search users..."
                          value={userSearchTerm}
                          onChange={(e) => setUserSearchTerm(e.target.value)}
                          className="pl-8 h-9 text-sm"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-52">
                      {filteredUsers.map(user => (
                        <div
                          key={user.id}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                          onClick={() => toggleSelection('users', user.id)}
                        >
                          <Checkbox checked={selectedUsers.includes(user.id)} />
                          <span className="text-sm">{user.first_name} {user.last_name}</span>
                        </div>
                      ))}
                      {filteredUsers.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">No users found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b bg-white">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold mb-2">Worklog Summary</h1>
              <p className="text-gray-600">Time aggregated by projects, users, and dates</p>
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

      {/* Statistics */}
      <div className="p-6 bg-gray-50 border-b">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="border-l-4 border-blue-500">
              <CardTitle className="text-sm font-medium text-gray-600">TOTAL ENTRIES</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{filteredWorklogs.length.toLocaleString()}</div>
              <p className="text-xs text-gray-500 mt-1">worklogs in period</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="border-l-4 border-green-500">
              <CardTitle className="text-sm font-medium text-gray-600">TOTAL HOURS</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {formatHours(Object.values(grandTotals).reduce((sum, h) => sum + h, 0))}
              </div>
              <p className="text-xs text-gray-500 mt-1">hours logged</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="border-l-4 border-purple-500">
              <CardTitle className="text-sm font-medium text-gray-600">GROUPS</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{Object.keys(pivotData).length}</div>
              <p className="text-xs text-gray-500 mt-1">primary groups</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="border-l-4 border-orange-500">
              <CardTitle className="text-sm font-medium text-gray-600">DATE RANGE</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold">{dateColumns.length}</div>
              <p className="text-xs text-gray-500 mt-1">{periodGrouping} in range</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Pivot Table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="bg-white rounded-lg shadow relative">
          <div className="overflow-auto max-h-[calc(100vh-400px)]">
            <table className="w-full border-collapse relative">
              <thead className="bg-gray-50 sticky top-0 z-20">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b-2 sticky left-0 bg-gray-50 z-30">
                    {groupBy.split('-')[0]}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b-2 bg-gray-50 z-20">
                    {groupBy.split('-')[1]}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b-2 bg-gray-50 z-20">
                    Total
                  </th>
                  {dateColumns.map(col => (
                    <th key={col.key} className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b-2 bg-gray-50 min-w-[80px] z-20">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(pivotData).map(([primaryKey, primaryData]) => (
                  <>
                    {/* Primary Group Row */}
                    <tr key={primaryKey} className="bg-blue-50 font-semibold">
                      <td className="px-4 py-3 text-sm text-gray-900 sticky left-0 bg-blue-50 z-10" colSpan="2">
                        {primaryData.label}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">
                        {formatHours(getRowTotal(primaryData.totals))}
                      </td>
                      {dateColumns.map(col => (
                        <td key={col.key} className="px-3 py-3 text-sm text-right text-gray-900">
                          {primaryData.totals[col.key] ? formatHours(primaryData.totals[col.key]) : '-'}
                        </td>
                      ))}
                    </tr>
                    
                    {/* Secondary Group Rows */}
                    {Object.entries(primaryData.children).map(([secondaryKey, secondaryData]) => (
                      <tr key={secondaryKey} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-500 sticky left-0 bg-white"></td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {secondaryData.label}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">
                          {formatHours(getRowTotal(secondaryData.totals))}
                        </td>
                        {dateColumns.map(col => (
                          <td key={col.key} className="px-3 py-3 text-sm text-right text-gray-700">
                            {secondaryData.totals[col.key] ? formatHours(secondaryData.totals[col.key]) : ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </>
                ))}
                
                {/* Grand Total Row */}
                <tr className="bg-gray-100 font-bold border-t-2 border-gray-300 sticky bottom-0 z-10">
                  <td className="px-4 py-3 text-sm text-gray-900 sticky left-0 bg-gray-100 z-20" colSpan="2">
                    GRAND TOTAL
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">
                    {formatHours(Object.values(grandTotals).reduce((sum, h) => sum + h, 0))}
                  </td>
                  {dateColumns.map(col => (
                    <td key={col.key} className="px-3 py-3 text-sm text-right text-gray-900">
                      {grandTotals[col.key] ? formatHours(grandTotals[col.key]) : '-'}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
